import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ArrowRight from '../../../../../assets/icons/arrows/ArrowRight';
import Button from '../../../../../components/Button';
import HighlightText from '../../../../../components/HighlightText';
import appContext from '../../../../../context/app/appContext';
import emissionSpoolsContext from '../../../../../context/emissionSpools/emissionSpoolsContext';
import { EmissionSpoolDto } from '../../../../../context/emissionSpools/emissionSpoolsType';
import {
    formatAmountWithSuffix,
    formatPercentages,
} from '../../../../../helpers/formats';
import { formatEther } from '@ethersproject/units';
import InfoPopUp from '../../../../../components/InfoPopUp';
import TextInput from '../../../../../components/TextInput';
import useLatestProposal from '../../../../../hooks/web3/vault/useLatestProposal';
import useUserAvailableVotingPower from '../../../../../hooks/web3/vault/useUserAvailableVotingPower';
import { debounce } from '../../../../../helpers/requests';
import { toBigNumber } from '../../../../../web3/utils';
import useIsMounted from '../../../../../hooks/useIsMounted';
import moment from 'moment';
// import useAPISpoolPriceFeed from '../../../../../hooks/web3/general/useAPISpoolPrice';

type Props = {
    spoolDetails: EmissionSpoolDto;
};

// TODO
const HARD_CODED = {
    '0x3c29eaf2618a18be91cddbd910393f188b2cfeb7': 0.1680585513736298,
    '0xf4a94c2baa3a117a2b69a35d8f0fcf58501c3200': 0.09492796141165909,
    '0xd7eca0b28063b7aecf9d39c68947b46c6d9dce88': 0.07281755034384346,
    '0xb58c8c9f7e1d13c93df187c8c7942ad4306b452a': 0.29836511924142567,
    '0x7ccc7a587f95b9359c5adfc652e254e3e30c0ae5': 0.011751132591217415,
    '0x19be3797868f237c8926d6a9d0da456b6fd98f7c': 0.00007252790340955594,
    '0x1effde5c76aa90791055d535761c4acb4e2b6c12': 0,
    '0x26ac1dba97c1f3f33eea9ad8b3e9d9dc588c023e': 0.13987835171452706,
    '0x33eedf0107d1859e4c74904e4ca95f564ec6c00d': 0,
    '0x3f5c39658a54f866db4081b10c4964246abe6b06': 8.537203831175966e-7,
    '0x409c6b0b314c1c8a344dd7154e89f9a7cb58bc7f': 0.17696754138486526,
    '0x47f66c098db4009d2ec7b3eb6f8e3c2e50b1eedb': 0.0036350647242441234,
    '0x599c1be6fed6c01db69281f3c36a6c983729a6f4': 0,
    '0x5b394fcd83a6c049fe5fd85aa4369b0d06959186': 0.007599410954758979,
    '0x83755af33bd100efbfce7eeb21ee4b628c422061': 0.006411312448029328,
    '0x9b83654edc734ecb9072eb03e70e55813f124c81': 0,
    '0xc3eea30418b53b6ec71e55223f751f7a67aa57a0': 0,
    '0xcf4cf54f1f9fc1a88c1b4d85d4eee5cdd8ed254b': 0,
    '0xf880ceebbbd960143a65ce70366324516f2104fc': 0.019514622188007282,
};

const EmissionSpoolsAccordionRow: React.FC<Props> = ({ spoolDetails }) => {
    const { address, apy, spoolName, allocAmount, allocWeight, tvr, depositedAmount } =
        spoolDetails;

    const { interfaceSDK } = useContext(appContext);

    // const { apiSpoolPriceFeed } = useAPISpoolPriceFeed();

    const {
        APY_BUFFER_TIME,
        totalInputAlloc,
        searchTerm,
        totalAllocAmount,
        userHasVoted,
        setTotalInputAlloc,
    } = useContext(emissionSpoolsContext);

    const [futureApy, setFutureApy] = useState(0);

    const isMounted = useIsMounted();

    const { userAvailableVotingPower } = useUserAvailableVotingPower();

    const { latestProposal } = useLatestProposal();

    const handleMaxButton = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
        const currentInputAlloc = Object.values(totalInputAlloc!).reduce(
            (accTotalAlloc, currTotalAlloc) => +accTotalAlloc + +currTotalAlloc,
            0
        );

        const leftoverAlloc =
            Math.floor(+(userAvailableVotingPower || 0)) - currentInputAlloc;

        if (totalInputAlloc[id]) {
            setTotalInputAlloc({
                [id]: (leftoverAlloc + +totalInputAlloc[id]).toString(),
            });
            return;
        }

        setTotalInputAlloc({ [id]: leftoverAlloc.toString() });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const { value } = e.target;

        if (value.match('^[1-9][0-9]*$') || !value) {
            const tempTotalInputAlloc = { ...totalInputAlloc, [id]: value };

            const totalAlloc = Object.values(tempTotalInputAlloc).reduce(
                (accTotalAlloc, currTotalAlloc) => +accTotalAlloc + +currTotalAlloc,
                0
            );

            if (totalAlloc > Math.floor(+(userAvailableVotingPower || 0))) return;

            setTotalInputAlloc({ [id]: value });
        }
    };

    const getCurrentSpoolWeight = useCallback(() => {
        if (userHasVoted) {
            return allocAmount / totalAllocAmount;
        }
        let userInputVotesTotal = 0;
        Object.values(totalInputAlloc).forEach((inputAlloc) => {
            userInputVotesTotal += +inputAlloc;
        });
        return (
            ((allocAmount || 0) + +totalInputAlloc[address]) /
            ((totalAllocAmount || 0) + userInputVotesTotal)
        );
    }, [allocAmount, totalInputAlloc, address, totalAllocAmount, userHasVoted]);

    const getFutureApy = useMemo(
        () =>
            debounce(async (currentSpoolWeight: number) => {
                const futureApyCalc = await interfaceSDK?.get.calculateFutureApy(
                    +formatEther(latestProposal!.plugins.emissionsAmount),
                    latestProposal!.plugins.emissionsDurations,
                    (currentSpoolWeight +
                        HARD_CODED[address.toLowerCase() as keyof typeof HARD_CODED]) /
                        2,
                    +depositedAmount
                    // apiSpoolPriceFeed ?? 0
                );

                isMounted() && setFutureApy(futureApyCalc || 0);
            }),
        [
            interfaceSDK,
            address,
            depositedAmount,
            latestProposal,
            // apiSpoolPriceFeed,
            isMounted,
        ]
    );

    const isAllowedToVote = useMemo(
        () =>
            !!(
                !userHasVoted &&
                userAvailableVotingPower &&
                !toBigNumber(userAvailableVotingPower).isZero() &&
                latestProposal &&
                latestProposal?.end > moment().unix()
            ),
        [userHasVoted, userAvailableVotingPower, latestProposal]
    );

    const remainingVotingPower = useMemo(() => {
        const totalAlloc = Object.values(totalInputAlloc!).reduce(
            (accTotalAlloc, currTotalAlloc) => +accTotalAlloc + +currTotalAlloc,
            0
        );

        return Math.floor(+(userAvailableVotingPower || 0)) - +totalAlloc;
    }, [userAvailableVotingPower, totalInputAlloc]);

    const apyBufferTimeEnded = useMemo(
        () => latestProposal && latestProposal?.end + APY_BUFFER_TIME < moment().unix(),
        [latestProposal, APY_BUFFER_TIME]
    );

    useEffect(() => {
        if (isNaN(getCurrentSpoolWeight())) {
            getFutureApy(0);
            return;
        }
        getFutureApy(getCurrentSpoolWeight());
    }, [
        allocAmount,
        totalAllocAmount,
        totalInputAlloc,
        getFutureApy,
        getCurrentSpoolWeight,
        isMounted,
    ]);

    return (
        <div className='row-container'>
            <div>
                <span>
                    {searchTerm ? (
                        <HighlightText text={spoolName} highlight={searchTerm} />
                    ) : (
                        <span className='highlight-text'>{spoolName}</span>
                    )}
                </span>
            </div>
            <div>
                <span className='column-label'>TVR</span>$
                {formatAmountWithSuffix(tvr || 0) || '0.00'}
            </div>
            <div className={`emission ${apyBufferTimeEnded && 'proposal-ended'}`}>
                <span className='column-label'>
                    {apyBufferTimeEnded ? 'Current Emission' : 'SPOOL APY'}
                </span>
                <div>
                    <span>{formatPercentages(+apy * 100 || 0)}</span>%
                    {!apyBufferTimeEnded && (
                        <>
                            <ArrowRight />
                            <span>
                                {isFinite(futureApy)
                                    ? formatPercentages(futureApy * 100 || 0)
                                    : '0.00'}
                            </span>
                            %
                        </>
                    )}
                </div>
            </div>
            <div className='alloc'>
                <div className='column-label'>
                    <span>My Allocations</span>
                    <InfoPopUp>
                        <span className='block'>
                            Your actionable voSPOOL:{' '}
                            {Math.round(+(userAvailableVotingPower || 0))}
                        </span>
                        <div className='mt'>
                            Only voSPOOL you earned at the exact time of the snapshot can
                            be used for voting.
                        </div>
                    </InfoPopUp>
                </div>
                <div>
                    <div className='input-container'>
                        <TextInput
                            name='allocation'
                            onChange={(e) => handleChange(e, address)}
                            value={totalInputAlloc?.[address]}
                            disabled={
                                !isAllowedToVote ||
                                (!remainingVotingPower && !totalInputAlloc?.[address])
                            }
                            placeholder='0'
                        />
                        <Button
                            onClick={(e) => handleMaxButton(e, address)}
                            variant='secondary'
                            disabled={!isAllowedToVote || !remainingVotingPower}
                        >
                            MAX
                        </Button>
                    </div>
                    <span>
                        <span className='alloc-percentage'>
                            {userAvailableVotingPower &&
                            !toBigNumber(userAvailableVotingPower).isZero()
                                ? formatPercentages(
                                      (+totalInputAlloc?.[address] * 100) /
                                          Math.floor(+userAvailableVotingPower)
                                  )
                                : '0.00'}
                        </span>
                        %
                    </span>
                </div>
            </div>
            <div className='total-alloc'>
                <span className='column-label'>Total Alloc</span>
                <div>
                    <div>
                        <InfoPopUp
                            trigger={
                                <span>{formatPercentages(allocWeight * 100 || 0)}%</span>
                            }
                        >
                            <span>Allocated voSPOOL: {Math.round(allocAmount || 0)}</span>
                        </InfoPopUp>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmissionSpoolsAccordionRow;
